/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'
import { useTranslation } from '@sailor/i18n-web'
import { useRequest } from 'ahooks'
import { useNavigate } from 'react-router-dom'
import { Button, Input, Select, FormPro } from '@roo/roo'
import { RegionDateRangePicker } from '@/components/RegionDatePicker'
import CommonTable from '@/components/CommonTable'
import TipText from '@/components/TipText'
import { getI18nTime, replaceMoneySymbol } from '@/common/utils/i18nFuncs'
import { downloadFile } from '@/common/utils'
import { fetchTransactionDetails, exportTransactionDetails, fetchBatchAdjustAsyncResult } from '@/pages/balance/utils/api'
import { ITransactionItem, ITransactionListParams, IExportTaskRes } from '@/pages/balance/utils/types'
import {
  ActionType,
  ACTION_TYPE_MULTI_MAP,
  getActionTypeOptions,
  ORDER_LINK_ACTION_TYPES,
  REMARK_ACTION_TYPES,
} from '@/pages/balance/utils/constants'
import { getDateTimePattern } from '@/common/utils/i18n'
import { failToast } from '@/common/utils/toast'
import { excelPolling } from '@/common/utils/polling'
import styles from './index.module.less'

interface TransactionDetailTabProps {
  /** 津贴策略编号 */
  budgetPolicyNo: string
  /** 从余额列表「变动明细」入口携带的初始搜索关键词（email 优先，无则取 phone） */
  defaultKeyword?: string
}

const TransactionDetailTab: React.FC<TransactionDetailTabProps> = ({
  budgetPolicyNo,
  defaultKeyword,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [accountInput, setAccountInput] = useState(defaultKeyword || '')
  const [nameInput, setNameInput] = useState('')
  const [timeRange, setTimeRange] = useState<[number, number] | null>(null)
  const [actionType, setActionType] = useState<ActionType>(ActionType.ALL)

  const [pageInfo, setPageInfo] = useState({ pageNo: 1, pageSize: 20 })
  const [filterParams, setFilterParams] = useState<Partial<ITransactionListParams>>(
    defaultKeyword ? { keyword: defaultKeyword } : {}
  )
  const [exportLoading, setExportLoading] = useState(false)

  // defaultKeyword 变化（Tab 切换时）重新注入并回显到搜索框
  useEffect(() => {
    if (defaultKeyword !== undefined) {
      setAccountInput(defaultKeyword)
      setFilterParams({ keyword: defaultKeyword })
      setPageInfo(prev => ({ ...prev, pageNo: 1 }))
    }
  }, [defaultKeyword])

  const queryParams: ITransactionListParams = {
    budgetPolicyNo,
    ...pageInfo,
    ...filterParams,
  }

  const { data: listRes, loading } = useRequest(
    () => fetchTransactionDetails(queryParams),
    { refreshDeps: [budgetPolicyNo, pageInfo, filterParams] }
  )

  const buildFilterParams = (): Partial<ITransactionListParams> => {
    const keyword = accountInput.trim() || nameInput.trim() || undefined
    const actionTypeList = actionType !== ActionType.ALL
      ? (ACTION_TYPE_MULTI_MAP[actionType] ?? [actionType])
      : undefined
    const params: Partial<ITransactionListParams> = { keyword, actionTypeList }
    if (timeRange && timeRange[0] && timeRange[1]) {
      params.startTime = Number(timeRange[0])
      params.endTime = Number(timeRange[1])
    }
    return params
  }

  const handleSearch = () => {
    setFilterParams(buildFilterParams())
    setPageInfo(prev => ({ ...prev, pageNo: 1 }))
  }

  const handleExport = async () => {
    try {
      setExportLoading(true)
      const fp = buildFilterParams()
      const exportParams: Parameters<typeof exportTransactionDetails>[0] = {
        budgetPolicyNo,
        keyword: fp.keyword,
        actionTypeList: fp.actionTypeList,
        startTime: fp.startTime,
        endTime: fp.endTime,
      }
      const res = await exportTransactionDetails(exportParams)
      handleExportResult(res)
    } catch (error: any) {
      failToast(error?.message || t('Keeta_EE_export_try_again_nDBU', '导出失败，请稍后重试！'))
      setExportLoading(false)
    }
  }

  const handleExportResult = (res: IExportTaskRes) => {
    if (!res.async && res.fileURL) {
      downloadFile({ url: res.fileURL })
      setExportLoading(false)
    } else if (res.async && res.taskToken) {
      excelPolling({
        requestFunction: () => fetchBatchAdjustAsyncResult(res.taskToken as string),
        onTimeoutChange: () => {},
        onFinish: (param) => {
          setExportLoading(false)
          if (param.type === 'success' && param.data?.fileUrl) {
            downloadFile({ url: param.data.fileUrl })
          } else {
            failToast(param.data?.message || t('Keeta_EE_export_try_again_nDBU', '导出失败，请稍后重试！'))
          }
        },
      })
    } else {
      setExportLoading(false)
    }
  }

  const actionTypeOptions = getActionTypeOptions(t)

  const columns = [
    {
      title: t('e_pc_account_id', '账号'),
      dataIndex: 'email',
      width: 200,
      render: (_: string, record: ITransactionItem) => (
        <TipText text={record.email || record.phone || '--'} />
      ),
    },
    {
      title: t('staff_name', '姓名'),
      dataIndex: 'staffName',
      width: 100,
      render: (text: string) => <TipText text={text || '--'} />,
    },
    {
      title: t('staff_department', '部门'),
      dataIndex: 'department',
      width: 140,
      render: (text: string) => <TipText text={text || '--'} />,
    },
    {
      title: t('e_pc_op_time', '操作时间'),
      dataIndex: 'bizTime',
      width: 160,
      render: (time: number) => getI18nTime(time, getDateTimePattern()),
    },
    {
      title: t('e_pc_amount_change', '金额变动'),
      dataIndex: 'amount',
      width: 120,
      render: (val: ITransactionItem['amount']) => {
        const rawText = val?.signedDisplayText || val?.displayText
        const text = rawText ? replaceMoneySymbol(rawText) : undefined
        if (!text) return '--'
        const isNegative = text.startsWith('-')
        const isPositive = text.startsWith('+')
        let color: string | undefined
        if (isNegative) color = '#f5222d'
        else if (isPositive) color = '#52c41a'
        return <span style={{ color, fontWeight: 500 }}>{text}</span>
      },
    },
    {
      title: t('e_pc_operator2', '操作人'),
      dataIndex: 'operator',
      width: 120,
      render: (text: string) => <TipText text={text || '--'} />,
    },
    {
      title: t('e_pc_op_type', '操作类型'),
      dataIndex: 'actionType',
      width: 180,
      render: (_: number, record: ITransactionItem) => (
        <TipText text={record.actionTypeDesc || '--'} />
      ),
    },
    {
      title: t('e_pc_remark2', '备注'),
      dataIndex: 'remark',
      width: 180,
      render: (_: string, record: ITransactionItem) => {
        const { actionType: type, remark, bizOrderId } = record

        if (REMARK_ACTION_TYPES.includes(type)) {
          return <TipText text={remark || '--'} />
        }

        if (ORDER_LINK_ACTION_TYPES.includes(type) && bizOrderId) {
          return (
            <span
              className={styles.detailLink}
              onClick={() => navigate(`/order?bizOrderId=${bizOrderId}`)}
            >
              {t('e_pc_order_details', '订单详情')}
            </span>
          )
        }

        if (type === ActionType.EXPIRY && bizOrderId) {
          return <span>{t('e_pc_type_refund_after_exp', '津贴失效后订单退款')}</span>
        }

        return '--'
      },
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* 筛选区：CSS Grid 4 列等分，日期区间占 2 列，与员工管理风格一致 */}
      <FormPro layout="vertical" size="small" labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
        <div className={styles.filterGrid} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 12px' }}>
          <FormPro.Item label={t('e_pc_account_id', '员工账号')} style={{ marginBottom: 8 }}>
            <Input
              allowClear
              style={{ width: '100%' }}
              value={accountInput}
              placeholder={t('e_pc_placeholder_account', '搜索邮箱或手机号')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAccountInput(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {if (e.key === 'Enter') handleSearch()}}
            />
          </FormPro.Item>
          <FormPro.Item label={t('staff_name', '员工姓名')} style={{ marginBottom: 8 }}>
            <Input
              allowClear
              style={{ width: '100%' }}
              value={nameInput}
              placeholder={t('e_pc_placeholder_name', '搜索姓名')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNameInput(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {if (e.key === 'Enter') handleSearch()}}
            />
          </FormPro.Item>
          {/* 操作时间占 2 列 */}
          <div style={{ gridColumn: 'span 2' }}>
            <FormPro.Item label={t('e_pc_op_time', '操作时间')} style={{ marginBottom: 8 }}>
              <RegionDateRangePicker
                clearable
                value={timeRange as any}
                style={{ width: '100%' }}
                onChange={(val: any) => setTimeRange(val)}
              />
            </FormPro.Item>
          </div>
          <FormPro.Item label={t('e_pc_op_type', '操作类型')} style={{ marginBottom: 8 }}>
            <Select
              value={actionType}
              style={{ width: '100%' }}
              options={actionTypeOptions}
              placeholder={t('e_pc_placeholder_select', '请选择')}
              onChange={(val: ActionType) => setActionType(val)}
            />
          </FormPro.Item>
          {/* 查询按钮：占第二行剩余 3 列，居右底部对齐 */}
          <div className={styles.actionColRight} style={{ gridColumn: 'span 3' }}>
            <Button type="brand" size="normal" className="btn-dark" onClick={handleSearch} style={{ marginBottom: 8 }}>
              {t('e_pc_aquire', '查询')}
            </Button>
          </div>
        </div>
      </FormPro>

      {/* 操作行：导出居右，位于搜索区与表格之间 */}
      <div className={styles.actionBar} style={{ marginTop: 16 }}>
        <div />
        <div className={styles.actionBarRight}>
          <Button className={styles.actionBarSecondaryBtn} loading={exportLoading} onClick={handleExport}>
            {t('e_pc_action_export', '导出')}
          </Button>
        </div>
      </div>

      {/* 表格 */}
      <CommonTable
        rowKey={(record: ITransactionItem) => `${record.staffId}-${record.bizTime}`}
        columns={columns}
        data={listRes?.result || []}
        loading={loading}
        style={{ marginTop: 16 }}
        pagination={{
          total: listRes?.totalCount,
          pageSize: pageInfo.pageSize,
          onChange: (pageNo: number, pageSize: number) => setPageInfo({ pageNo, pageSize }),
        }}
      />
    </div>
  )
}

export default TransactionDetailTab
